import { handleize } from '@utilities/theme';

/**
 * NOSTO UTILITIES
 *
 * Comprehensive integration layer between Nosto's search/recommendation platform and Shopify's native APIs.
 * Provides seamless data transformation, query building, and vendor switching capabilities.
 *
 * Used by: product-grid.js, filter-drawer.js, predictive-search.js
 *
 * Key Features:
 * - Bidirectional field mapping between Nosto and Shopify formats
 * - Query building with client-specific preFilters (Greyson customizations)
 * - Data transformation for products, filters, and predictive search
 * - Vendor switching with automatic component reloading
 * - Error handling and fallback mechanisms
 * - Aggressive preloading to reduce LCP delays
 *
 * Client-Specific Customizations (Greyson):
 * - greyson-availability field filtering
 * - Custom color family mappings
 * - DataExtractor integration for enhanced metadata
 * - Brand-specific product hiding rules
 *
 * @namespace NostoUtilities
 */

// Global Nosto request queue for aggressive preloading
const _nostoRequestQueue = new Map();
let _nostoLibraryReady = false;
let _nostoReadyPromise = null;

// Library availability checking to prevent duplicate timers
let _nostoAvailabilityCheck = null;

/**
 * Nosto Error Types for user-friendly error handling
 * @type {Object.<string, Object>}
 * @private
 */
const _nostoErrorTypes = {
  LIBRARY_TIMEOUT: {
    code: 'NOSTO_WRAPPER_LIBRARY_TIMEOUT',
    message: 'Search is temporarily unavailable. Please refresh the page and try again.',
    userMessage: 'Search is temporarily unavailable. Please refresh the page and try again.',
    debugMessage: 'Nosto library failed to load within timeout'
  },
  REQUEST_TIMEOUT: {
    code: 'NOSTO_WRAPPER_REQUEST_TIMEOUT',
    message: 'Loading is taking longer than expected. Please try again.',
    userMessage: 'Loading is taking longer than expected. Please try again.',
    debugMessage: 'Nosto API request timed out'
  },
  API_ERROR: {
    code: 'NOSTO_WRAPPER_API_ERROR',
    message: 'Content is currently unavailable. Please try again in a moment.',
    userMessage: 'Content is currently unavailable. Please try again in a moment.',
    debugMessage: 'Nosto API returned an error'
  },
  NETWORK_ERROR: {
    code: 'NOSTO_WRAPPER_NETWORK_ERROR',
    message: 'Unable to connect to Nosto service. Please check your connection and try again.',
    userMessage: 'Unable to connect to Nosto service. Please check your connection and try again.',
    debugMessage: 'Network error occurred'
  },
  CONFIGURATION_ERROR: {
    code: 'NOSTO_WRAPPER_CONFIG_ERROR',
    message: 'Configuration error. Please contact support if this persists.',
    userMessage: 'Configuration error. Please contact support if this persists.',
    debugMessage: 'Nosto configuration or query building error'
  }
};

/**
 * Create a Nosto error with user-friendly messaging
 * @param {string} errorType - Key from _nostoErrorTypes
 * @param {Object} [details] - Additional error details
 * @param {Error} [originalError] - Original error object for debugging
 * @returns {Error} - Enhanced error with user-friendly message
 * @private
 */
const _createNostoError = (errorType, details = {}, originalError = null) => {
  const errorInfo = _nostoErrorTypes[errorType];
  if (!errorInfo) {
    // Fallback for unknown error types
    return new Error('An unexpected error occurred. Please try again.');
  }

  const error = new Error(errorInfo.message);

  // Add error metadata for debugging and handling
  error.code = errorInfo.code;
  error.userMessage = errorInfo.userMessage;
  error.debugMessage = errorInfo.debugMessage;
  error.details = details;
  error.originalError = originalError;

  // Preserve original error stack if available
  if (originalError?.stack) {
    error.stack = originalError.stack;
  }

  return error;
};

/**
 * Initialize Nosto preloading when library becomes available
 * @private
 */
const _initNostoPreloading = () => {
  if (_nostoLibraryReady) {
    console.log('Nosto Wrapper: Preloading already initialized');
    return;
  }

  console.log('Nosto Wrapper: Initializing preloading with 20s timeout, 100ms poll interval');
  const preloadStartTime = performance.now();

  _nostoReadyPromise = nostoAvailableCheck({ timeout: 20000, pollInterval: 100 })
    .then(() => {
      const elapsed = performance.now() - preloadStartTime;
      _nostoLibraryReady = true;
      console.log(`Nosto Wrapper: Library ready after ${elapsed.toFixed(2)}ms, processing ${Array.from(_nostoRequestQueue).length} queued requests`);

      // Process any queued requests
      for (const [key, { query, options, resolve, reject }] of _nostoRequestQueue) {
        _executeNostoRequest(key, query, options)
          .then(resolve)
          .catch(reject);
      }
      _nostoRequestQueue.clear();
      console.log('Nosto Wrapper: All queued requests processed');
    })
    .catch(error => {
      const elapsed = performance.now() - preloadStartTime;
      console.warn(`Nosto Wrapper: Library failed to load after ${elapsed.toFixed(2)}ms, rejecting ${Array.from(_nostoRequestQueue).length} queued requests:`, error);

      // Reject all queued requests
      for (const [key, { reject }] of _nostoRequestQueue) {
        reject(new Error('Nosto library failed to load within timeout'));
      }
      _nostoRequestQueue.clear();
    });
};

/**
 * Execute a Nosto request with caching
 * @param {string} cacheKey - Cache key for the request
 * @param {Object} query - Nosto query
 * @param {Object} options - Nosto options
 * @returns {Promise<Object>} Nosto response
 * @private
 */
const _executeNostoRequest = (cacheKey, query, options) => {
  return new Promise((resolve, reject) => {
    const startTime = performance.now();

    const timeoutId = setTimeout(() => {
      const elapsed = performance.now() - startTime;
      console.warn(`Nosto Wrapper: Request '${cacheKey}' timed out after ${elapsed.toFixed(2)}ms`);
      const timeoutError = new Error(`Nosto request timed out after ${elapsed.toFixed(2)}ms`);
      reject(_createNostoError('REQUEST_TIMEOUT', { cacheKey, elapsed }, timeoutError));
    }, 20000);

    const cleanup = () => {
      clearTimeout(timeoutId);
    };

    window.nostojs((api) => {
      api
        .search(query, options)
        .then((response) => {
          const elapsed = performance.now() - startTime;
          console.log(`Nosto Wrapper: Request '${cacheKey}' completed successfully in ${elapsed.toFixed(2)}ms`);
          cleanup();
          resolve(response);
        })
        .catch((error) => {
          const elapsed = performance.now() - startTime;
          console.error(`Nosto Wrapper: Request '${cacheKey}' failed after ${elapsed.toFixed(2)}ms:`, error);
          cleanup();
          const apiError = new Error(`Nosto search failed: ${error.message}`);
          reject(_createNostoError('API_ERROR', { cacheKey, elapsed }, apiError));
        });
    });
  });
};

// Start preloading initialization immediately
if (typeof window !== 'undefined') {
  // Use requestIdleCallback if available, otherwise setTimeout
  const scheduleInit = window.requestIdleCallback ||
    ((callback) => setTimeout(callback, 1));

  scheduleInit(_initNostoPreloading);
}

/**
 * Direct mapping from Nosto field names to Shopify filter parameters
 * @type {Object.<string, string>}
 * @private
 */
const _filterMap = {
  // Product-level availability
  'available': 'filter.v.availability',
  'customFields.greyson-availability': 'filter.p.m.greyson.availability',

  // Variant-level availability
  'skus.customFields.greyson-availability': 'filter.v.m.greyson.availability',

  // Product type
  'customFields.producttype': 'filter.p.product_type',

  // Price
  'price': 'filter.p.price',

  // Variant options (color, size, inseam, waist, value)
  'skus.customFields.color': 'filter.v.option.color',
  'skus.customFields.size': 'filter.v.option.size',
  'skus.customFields.inseam': 'filter.v.option.inseam',
  'skus.customFields.waist': 'filter.v.option.waist',
  'skus.customFields.value': 'filter.v.option.value',

  // Variant custom options (color family)
  'skus.customFields.greyson-color_family': 'filter.v.m.greyson.color_family',

  // Variant custom options w/ availability logic
  'skus.customFields.greyson-available_color': 'filter.v.m.greyson.available_color',
  'skus.customFields.greyson-available_color_family': 'filter.v.m.greyson.available_color_family',
  'skus.customFields.greyson-available_size': 'filter.v.m.greyson.available_size',
  'skus.customFields.greyson-available_inseam': 'filter.v.m.greyson.available_inseam',

  // Product-level custom metafields
  'customFields.custom-pattern': 'filter.p.m.custom.pattern',
  'customFields.custom-sleeve_length': 'filter.p.m.custom.sleeve_length',
  'customFields.custom-team': 'filter.p.m.custom.team',
  'customFields.greyson-available_price_min': 'filter.p.m.greyson.available_price_min',
  'customFields.greyson-available_price_max': 'filter.p.m.greyson.available_price_max',

  // Data Extractor facets for Shopify filters that are stored in a JSON array
  // department
  'extra.DataExtractor_custom-department': 'filter.p.m.custom.department',
  // aggregated lists based on availability logic
  'extra.DataExtractor_greyson-available_color_families': 'filter.p.m.greyson.available_color_families',
  'extra.DataExtractor_greyson-available_colors': 'filter.p.m.greyson.available_colors',
  'extra.DataExtractor_greyson-available_sizes': 'filter.p.m.greyson.available_sizes',
  'extra.DataExtractor_greyson-available_inseams': 'filter.p.m.greyson.available_inseams',
  'extra.DataExtractor_greyson-unavailable_color_families': 'filter.p.m.greyson.unavailable_color_families',
  'extra.DataExtractor_greyson-unavailable_colors': 'filter.p.m.greyson.unavailable_colors',
  'extra.DataExtractor_greyson-unavailable_sizes': 'filter.p.m.greyson.unavailable_sizes',
  'extra.DataExtractor_greyson-unavailable_inseams': 'filter.p.m.greyson.unavailable_inseams'
};


/**
 * Build dynamic preFilter based on context and settings
 * @param {Object} options - Configuration options
 * @param {boolean} options.allowSoldOutStateProducts - Whether to include products tagged shopify:unavailable (default: false)
 * @returns {Array} - preFilter array for Nosto query
 * @private
 */
const _buildPreFilter = ({ allowSoldOutStateProducts = false, categoryId = null } = {}) => {
  const preFilterConditions = [
    // Always require greyson-availability to be true (product-level aggregate)
    // If product-level is true, it means at least one variant is available
    {
      field: "customFields.greyson-availability",
      value: ["true"]
    },
    // Always exclude products with the strict hide tags
    // While we move to extended variant support, let's stop Nosto from returning Complex Product and Child Product tagged products.
    {
      not: [{
        field: "tags1",
        value: [
          "nosto:hide",
          "theme:hide",
          "app only",
          "status:unlisted",
          "Complex Product",
          "Child Product"
        ]
      }]
    }
  ];
  // Conditionally exclude shopify:unavailable products if we explicitly exclude them.
  // The default behavior is to exclude sold out products.
  if (!allowSoldOutStateProducts) {
    // console.log("Extra Exclusion: Excluding sold out products from the Nosto query");
    preFilterConditions.push({
      not: [{
        field: "tags1",
        value: ["shopify:unavailable"]
      }]
    });
  }
  // Condiitonally exclude products with the tag `search:hide` if the categoryId is empty. Only a collection render would provide the categoryId.
  // Search page and predictive search bar should reject any products with the tag `search:hide`.
  if (!categoryId) {
    // console.log("Extra Exclusion: Excluding products with the tag `search:hide` from the Nosto query");
    preFilterConditions.push({
      not: [{
        field: "tags1",
        value: ["search:hide"]
      }]
    });
  }
  return [{
    all: preFilterConditions
  }];
};

/**
 * Default field selections for different Nosto query types
 * @type {Object.<string, Array<string>>}
 * @private
 */
const _defaultFields = {
  products: [
    "pid",
    "name",
    "url",
    "imageUrl",
    "productId",
    "productType",
    "variantId",
    "price",
    "tags1",
    "customFields.key",
    "customFields.value",
    "skus.id",
    "skus.name",
    "skus.imageUrl",
    "skus.price",
    "skus.customFields.key",
    "skus.customFields.value",
  ],
  minimal: ["pid"], // For filter-only queries
  predictive: [
    "pid",
    "productId",
    "name",
    "url",
    "productType",
    "imageUrl",
    "variantId",
    "tags1",
    "customFields.key",
    "customFields.value",
    "skus.id",
    "skus.name",
    "skus.imageUrl",
    "skus.price",
    "skus.customFields.key",
    "skus.customFields.value",
  ],
  keywords: ["keyword"]
};

/**
 * Static sort mapping for better performance
 * Maps Shopify sort options to Nosto sort format
 * @type {Object.<string, Array<Object>>}
 * @private
 */
const _sortMapping = {
  "price-ascending": [{ field: "customFields.greyson-available_price_min", order: "asc" }],
  "price-descending": [{ field: "customFields.greyson-available_price_max", order: "desc" }],
};

/**
 * Cache for handle extraction to avoid repeated URL parsing
 * @type {Map}
 * @private
 */
let _handleCache;

/**
 * Cache for filter label transformations to avoid repeated processing
 * @type {Map}
 * @private
 */

/**
 * Cache for reverse filter mapping to avoid repeated object entries iteration
 * @type {Map}
 * @private
 */
let _reverseFilterMap;

/**
 * Transform Shopify boolean values to Nosto boolean values
 * @param {Array<string>} values - Array of filter values
 * @returns {Array<string>} - Transformed values with Shopify booleans converted to Nosto format
 * @private
 */
const transformShopifyBooleansToNosto = (values) => {
  const transformedValues = [];

  for (const value of values) {
    // Add the original value
    transformedValues.push(value);

    // Add transformed boolean values if applicable
    if (value === '1') {
      transformedValues.push('true');
    } else if (value === '0') {
      transformedValues.push('false');
    } else if (value === 'true') {
      transformedValues.push('1');
    } else if (value === 'false') {
      transformedValues.push('0');
    }
  }

  // Remove duplicates and return
  return [...new Set(transformedValues)];
};

/**
 * Wait for Nosto library to be available with retry mechanism
 *
 * Uses setTimeout-based polling instead of requestAnimationFrame for consistent
 * behavior across all browser contexts, including:
 * - Instagram webview (known to throttle rAF)
 * - Background tabs
 * - Low-power mode browsers
 *
 * @param {Object} options - Configuration options
 * @param {number} options.timeout - Overall timeout in ms (default: 20000)
 * @param {number} options.pollInterval - Interval between checks in ms (default: 100)
 * @returns {Promise<boolean>} - Resolves when Nosto is available
 * @private
 */
const nostoAvailableCheck = ({ timeout = 20000, pollInterval = 100 } = {}) => {
  // Return existing promise if already checking
  if (_nostoAvailabilityCheck) {
    return _nostoAvailabilityCheck;
  }

  _nostoAvailabilityCheck = new Promise((resolve, reject) => {
    // Check immediately first
    if (typeof window.nostojs !== "undefined") {
      console.log('Nosto Wrapper: Library already available');
      _nostoAvailabilityCheck = null; // Reset for next check
      resolve(true);
      return;
    }

    console.log(`Nosto Wrapper: Waiting for library (timeout: ${timeout}ms, pollInterval: ${pollInterval}ms)`);
    const startTime = performance.now();
    let checkCount = 0;
    let intervalId = null;
    let timeoutId = null;

    const cleanup = () => {
      if (intervalId) {
        clearInterval(intervalId);
        intervalId = null;
      }
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
      _nostoAvailabilityCheck = null; // Reset for next check
    };

    // Single timeout mechanism - no competing retry limits
    timeoutId = setTimeout(() => {
      const elapsed = performance.now() - startTime;
      console.warn(`Nosto Wrapper: Library availability check timed out after ${elapsed.toFixed(2)}ms (${checkCount} checks)`);
      cleanup();
      const timeoutError = new Error(`Nosto library not available after ${elapsed.toFixed(2)}ms timeout`);
      reject(_createNostoError('LIBRARY_TIMEOUT', { elapsed, checkCount }, timeoutError));
    }, timeout);

    // Use setInterval for consistent, predictable polling
    // Unlike requestAnimationFrame, setInterval:
    // - Has consistent timing across webviews and background tabs
    // - Is not tied to the rendering cycle
    // - Works reliably in Instagram's webview
    const checkNosto = () => {
      checkCount++;

      if (typeof window.nostojs !== "undefined") {
        const elapsed = performance.now() - startTime;
        console.log(`Nosto Wrapper: Library became available after ${elapsed.toFixed(2)}ms (${checkCount} checks)`);
        cleanup();
        resolve(true);
      }
      // If not found, interval continues until timeout fires
    };

    // Start polling with consistent intervals
    intervalId = setInterval(checkNosto, pollInterval);

    // Also check immediately after a short delay to catch fast loads
    setTimeout(checkNosto, 10);
  });

  return _nostoAvailabilityCheck;
};

/**
 * Make a Nosto search API call with proper error handling and aggressive preloading
 * @param {Object} query - The Nosto search query
 * @param {Object} options - The Nosto search options
 * @param {Object} config - Configuration options
 * @param {number} config.timeout - Request timeout in ms (default: 20000)
 * @param {boolean} config.debug - Enable debug logging (default: false)
 * @param {string} config.trigger - The trigger for the Nosto search
 * @returns {Promise<Object>} - The Nosto response
 */
/**
 * Get the current Nosto library state
 * @returns {Object} State object with ready status and execution strategy
 * @private
 */
const _getNostoLibraryState = () => {
  // Check if library is fully ready
  if (_nostoLibraryReady && typeof window.nostojs !== "undefined") {
    return { ready: true, strategy: 'immediate' };
  }

  // Check if library is loading
  if (_nostoReadyPromise) {
    return { ready: false, strategy: 'wait_promise', promise: _nostoReadyPromise };
  }

  // Fallback: need to wait for library
  return { ready: false, strategy: 'wait_check' };
};

export const nostoSearch = async (query, options, { timeout = 20000, debug = false, trigger = '' } = {}) => {
  // debug = true;

  if (debug) {
    console.log(`Nosto search query: ${trigger}`, query);
    console.log(`Nosto search options: ${trigger}`, options);
  }

  const state = _getNostoLibraryState();

  try {
    if (state.ready) {
      if (debug) {
        console.log(`Nosto Wrapper: Executing ${trigger} immediately (library ready)`);
      }
      return await _executeNostoRequest(`${trigger}_${Date.now()}`, query, options);
    }

    if (state.strategy === 'wait_promise') {
      if (debug) {
        console.log(`Nosto Wrapper: ${trigger} waiting for library promise`);
      }
      await state.promise;
      if (debug) {
        console.log(`Nosto Wrapper: ${trigger} library ready, executing now`);
      }
      return await _executeNostoRequest(`${trigger}_${Date.now()}`, query, options);
    }

    // Strategy: wait_check - use nostoAvailableCheck
    if (debug) {
      console.log(`Nosto Wrapper: ${trigger} waiting for library availability check`);
    }
    await nostoAvailableCheck({ timeout });

    return new Promise((resolve, reject) => {
      const timeoutId = setTimeout(() => {
        reject(new Error(`Nosto search request timed out after ${timeout}ms`));
      }, timeout);

      const cleanup = () => {
        clearTimeout(timeoutId);
      };

      window.nostojs((api) => {
        api
          .search(query, options)
          .then((response) => {
            cleanup();
            if (debug) {
              console.log(`Nosto search response: ${trigger}`, response);
            }
            resolve(response);
          })
          .catch((error) => {
            cleanup();
            // Preserve user-friendly error messages from _createNostoError
            if (error.code && error.userMessage) {
              reject(error); // Pass through the enhanced error object
            } else {
              const apiError = new Error(`Nosto search failed: ${trigger} ${error.message}`);
              reject(_createNostoError('API_ERROR', { trigger }, apiError));
            }
          });
      });
    });
  } catch (error) {
    // Preserve user-friendly error messages from _createNostoError
    if (error.code && error.userMessage) {
      throw error; // Re-throw the enhanced error object
    } else {
      throw _createNostoError('API_ERROR', { trigger }, error);
    }
  }
};

/**
 * Resolve a recommendation campaign from a Nosto recommendations response.
 *
 * Supports the two common response shapes:
 * - response.recommendations[placementId]
 * - response.campaigns.recommendations[placementId]
 *
 * @param {Object} response - Nosto recommendations response object
 * @param {string} placementId - Placement id / element id
 * @returns {Object|null} Recommendation campaign object
 */
export const nostoGetRecommendationCampaign = (response, placementId = '') => {
  if (!response) return null;

  const recommendations =
    response?.recommendations ||
    response?.campaigns?.recommendations ||
    {};

  if (placementId && recommendations[placementId]) {
    return recommendations[placementId];
  }

  if (placementId) {
    const matchedCampaign = Object.values(recommendations).find((campaign) => {
      return campaign?.div_id === placementId;
    });

    if (matchedCampaign) {
      return matchedCampaign;
    }
  }

  return Object.values(recommendations)[0] || null;
};

/**
 * Normalize recommendation product URLs to storefront-relative paths.
 *
 * Nosto responses can vary between `url`, `product_url`, `link`, or absolute
 * canonical URLs. Product cards should always fetch product JSON from the
 * current storefront origin.
 *
 * @param {Object} product - Recommendation product object
 * @returns {string} Relative product URL path (or empty string when unavailable)
 */
const nostoNormalizeRecommendationProductUrl = (product = {}) => {
  const rawUrl = String(
    product?.url ||
      product?.productUrl ||
      product?.product_url ||
      product?.link ||
      product?.href ||
      '',
  ).trim();

  const fallbackHandle = String(
    product?.handle || product?.productHandle || product?.slug || '',
  ).trim();

  if (!rawUrl) {
    return fallbackHandle ? `/products/${fallbackHandle}` : '';
  }

  try {
    const baseOrigin =
      typeof window !== 'undefined' && window.location?.origin
        ? window.location.origin
        : 'https://example.com';
    const parsedUrl = new URL(rawUrl, baseOrigin);
    return `${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`;
  } catch (error) {
    if (rawUrl.startsWith('/')) {
      return rawUrl;
    }
    return fallbackHandle ? `/products/${fallbackHandle}` : rawUrl;
  }
};

/**
 * Validate product-level availability flag for recommendation items.
 *
 * Recommendation responses can include product-level `custom_fields` where
 * `greyson-availability` indicates whether the item should be rendered.
 *
 * @param {Object} product - Recommendation product object
 * @returns {boolean} True when product-level availability is explicitly true
 */
const nostoIsRecommendationProductAvailable = (product = {}) => {
  const productCustomFields = product?.custom_fields || product?.customFields || {};
  const availabilityValue =
    productCustomFields?.['greyson-availability'] ??
    productCustomFields?.greysonAvailability ??
    '';

  if (availabilityValue === true) {
    return true;
  }

  return String(availabilityValue).trim().toLowerCase() === 'true';
};

/**
 * Resolve a valid default variant id for recommendation products.
 *
 * Recommendation payloads can include non-variant product-level fields like
 * `variant_id: "USD"`. We intentionally resolve from SKU-level ids and choose
 * the first SKU flagged as available (`available` or `greyson-availability`)
 * to mirror product-grid and predictive-search behavior.
 *
 * @param {Object} product - Recommendation product object
 * @returns {number|null} Variant id for product-card bootstrap
 */
const nostoResolveRecommendationDefaultVariantId = (product = {}) => {
  const parseCandidate = (value) => {
    const parsed = parseInt(value, 10);
    return Number.isFinite(parsed) ? parsed : null;
  };

  const isTruthyFlag = (value) => {
    return value === true || String(value || '').toLowerCase() === 'true';
  };

  const skus = Array.isArray(product?.skus) ? product.skus : [];
  let firstNumericSkuId = null;

  for (const sku of skus) {
    const skuCandidates = [
      sku?.variant_id,
      sku?.variantId,
      sku?.id,
      sku?.sku_id,
      sku?.skuId,
    ];

    for (const candidate of skuCandidates) {
      const parsedCandidate = parseCandidate(candidate);
      if (parsedCandidate === null) {
        continue;
      }

      if (firstNumericSkuId === null) {
        firstNumericSkuId = parsedCandidate;
      }

      const customFields = sku?.custom_fields || sku?.customFields || {};
      const customAvailability =
        customFields?.['greyson-availability'] ||
        customFields?.greysonAvailability ||
        '';

      if (isTruthyFlag(sku?.available) || isTruthyFlag(customAvailability)) {
        return parsedCandidate;
      }
    }
  }

  return firstNumericSkuId;
};

/**
 * Transform a Nosto recommendation campaign into the theme's product format.
 *
 * @param {Object} campaign - Nosto recommendation campaign object
 * @returns {Array<Object>} Array of normalized product objects
 */
export const nostoTransformRecommendationCampaignToProducts = (campaign = {}) => {
  const recommendationProducts = Array.isArray(campaign?.products)
    ? campaign.products
    : [];

  const normalizedProducts = [];

  for (let i = 0; i < recommendationProducts.length; i++) {
    const product = recommendationProducts[i];

    if (!nostoIsRecommendationProductAvailable(product)) {
      continue;
    }

    const productId = parseInt(
      product?.productId || product?.product_id || product?.id || product?.pid,
      10,
    );

    if (!Number.isFinite(productId)) {
      continue;
    }

    const url = nostoNormalizeRecommendationProductUrl(product);
    const handle = product?.handle || product?.productHandle || extractHandleFromUrl(url);

    if (!url) {
      continue;
    }

    const defaultVariantId = nostoResolveRecommendationDefaultVariantId(product);

    normalizedProducts.push({
      id: productId,
      handle,
      title: product?.name || product?.title || '',
      url,
      imageUrl: product?.imageUrl || product?.image_url || '',
      defaultVariantId: Number.isFinite(defaultVariantId)
        ? defaultVariantId
        : null,
      tags: Array.isArray(product?.tags1)
        ? product.tags1
        : Array.isArray(product?.tags)
          ? product.tags
          : [],
    });
  }

  return normalizedProducts;
};

/**
 * Request recommendation data from Nosto's recommendation request API.
 *
 * Uses createRecommendationRequest() for non-SPA theme usage and returns
 * normalized products that match existing theme product-card contracts.
 *
 * @param {Object} config - Request config
 * @param {string} config.placementId - Recommendation placement id
 * @param {number|string} config.productId - Current product id
 * @param {string} [config.intent] - Recommendation intent (related/complementary)
 * @param {number} [config.limit] - Max recommendation products
 * @param {boolean} [config.debug] - Debug logging
 * @param {number} [config.timeout] - Timeout in ms
 * @returns {Promise<Object>} Normalized recommendation response
 */
export const nostoCreateRecommendationRequest = async ({
  placementId = '',
  productId,
  intent = 'related',
  limit = 10,
  debug = false,
  timeout = 20000,
} = {}) => {
  if (!placementId) {
    throw _createNostoError(
      'CONFIGURATION_ERROR',
      { placementId },
      new Error('Nosto recommendation placement id is required'),
    );
  }

  if (!productId) {
    throw _createNostoError(
      'CONFIGURATION_ERROR',
      { productId },
      new Error('Nosto recommendation product id is required'),
    );
  }

  await nostoAvailableCheck({ timeout });

  return new Promise((resolve, reject) => {
    const timeoutId = setTimeout(() => {
      const timeoutError = new Error(
        `Nosto recommendation request timed out after ${timeout}ms`,
      );
      reject(_createNostoError('REQUEST_TIMEOUT', { placementId, productId }, timeoutError));
    }, timeout);

    const cleanup = () => {
      clearTimeout(timeoutId);
    };

    window.nostojs((api) => {
      try {
        const requestBuilder = api
          .createRecommendationRequest({ includeTagging: true })
          .setPageType('product')
          .setProducts([{ product_id: String(productId) }])
          .setElements([placementId])
          .setResponseMode('JSON_ORIGINAL');

        if (debug) {
          console.log('Nosto recommendation request config', {
            placementId,
            productId: String(productId),
            intent,
            limit,
          });
        }

        requestBuilder
          .load({ includeTagging: true })
          .then((response) => {
            cleanup();

            const campaign = nostoGetRecommendationCampaign(response, placementId);
            const transformedProducts =
              nostoTransformRecommendationCampaignToProducts(campaign);
            const products = transformedProducts.slice(0, Math.max(1, limit));

            if (debug) {
              const missingUrlCount = transformedProducts.filter((product) => !product.url).length;
              const missingVariantCount = transformedProducts.filter(
                (product) => !Number.isFinite(product.defaultVariantId),
              ).length;
              console.log('Nosto recommendation response', {
                placementId,
                resultId: campaign?.result_id || null,
                totalProducts: transformedProducts.length,
                returnedProducts: products.length,
                missingUrlCount,
                missingVariantCount,
                sampleUrls: products.slice(0, 3).map((product) => product.url),
                sampleVariantIds: products
                  .slice(0, 3)
                  .map((product) => product.defaultVariantId),
              });
            }

            resolve({
              vendor: 'nosto',
              placementId,
              resultId: campaign?.result_id || null,
              campaign,
              products,
            });
          })
          .catch((error) => {
            cleanup();
            const apiError = new Error(
              `Nosto recommendation request failed: ${error?.message || 'Unknown error'}`,
            );
            reject(
              _createNostoError(
                'API_ERROR',
                { placementId, productId: String(productId), intent },
                apiError,
              ),
            );
          });
      } catch (error) {
        cleanup();
        const requestError = new Error(
          `Failed to build Nosto recommendation request: ${error?.message || 'Unknown error'}`,
        );
        reject(
          _createNostoError(
            'CONFIGURATION_ERROR',
            { placementId, productId: String(productId), intent },
            requestError,
          ),
        );
      }
    });
  });
};

/**
 * Register Nosto's campaign click attribution listener for a rendered campaign container.
 *
 * This should be called when rendering recommendation results from JSON mode.
 *
 * @param {HTMLElement} element - The campaign container element
 * @param {Object} campaign - Campaign object from Nosto response
 * @param {Object} options - Configuration options
 * @param {boolean} [options.debug=false] - Enable debug logging
 */
export const nostoAttributeProductClicksInCampaign = async (
  element,
  campaign,
  { debug = false } = {},
) => {
  if (!element || !campaign) return;

  await nostoAvailableCheck();

  window.nostojs((api) => {
    try {
      api.attributeProductClicksInCampaign(element, campaign);
      if (debug) {
        console.log('Nosto campaign click attribution attached', {
          resultId: campaign?.result_id || null,
          divId: campaign?.div_id || null,
        });
      }
    } catch (error) {
      if (debug) {
        console.error('Failed to attach Nosto campaign click attribution', error);
      }
    }
  });
};

/**
 * Record attribution event with Nosto
 * Core wrapper for api.recordAttribution() with proper error handling
 *
 * @see https://docs.nosto.com/techdocs/apis/frontend/js-apis/record-attribution
 *
 * @param {Object} options - Attribution options
 * @param {string} options.type - Event type (e.g., "vp", "cp", "is", "vc"). See Nosto docs for full list.
 * @param {string} options.target - Target ID (product ID, search term, category ID)
 * @param {string} [options.ref] - Recommendation/slot ID that contained the link which triggered the event
 * @param {string} [options.refType] - Type of Nosto feature that contributed to the attribution (e.g., "cmp", "os", "rec", "api")
 * @param {string} [options.refSrc] - Product ID of parent element of the link that triggered the event
 * @param {string} [options.targetFragment] - The skuId in case of "vp" events
 * @param {boolean} [options.debug=false] - Enable debug logging
 * @param {number} [options.timeout=20000] - Request timeout in ms
 * @returns {Promise<void>} - Resolves when attribution is recorded (non-blocking)
 */
export const nostoRecordAttribution = async ({ type, target, ref = null, refType = null, refSrc = null, targetFragment = null, debug = false, timeout = 20000 } = {}) => {
  // debug = true;
  try {
    // Wait for Nosto to be available
    await nostoAvailableCheck({ timeout });

    return new Promise((resolve, reject) => {
      const timeoutId = setTimeout(() => {
        reject(new Error(`Nosto attribution request timed out after ${timeout}ms`));
      }, timeout);

      const cleanup = () => {
        clearTimeout(timeoutId);
      };

      if (debug) {
        console.log(`Nosto attribution: ${type}`, { type, target, ref, refType, refSrc, targetFragment });
      }

      window.nostojs((api) => {
        const attributionParams = { type, target };
        if (ref) {
          attributionParams.ref = ref;
        }
        if (refType) {
          attributionParams.refType = refType;
        }
        if (refSrc) {
          attributionParams.refSrc = refSrc;
        }
        if (targetFragment) {
          attributionParams.targetFragment = targetFragment;
        }

        try {
          api
            .recordAttribution(attributionParams)
            .done(() => {
              cleanup();
              if (debug) {
                console.log(`Nosto attribution recorded: ${type}`, { type, target, ref, refType, refSrc, targetFragment });
              }
              resolve();
            });
        } catch (error) {
          cleanup();
          reject(new Error(`Nosto attribution failed for type '${type}': ${error.message || 'Unknown error'}`));
        }
      });
    });
  } catch (error) {
    throw new Error(`Nosto attribution failed for type '${type}': ${error.message}`);
  }
};

/**
 * Record search add-to-cart event with Nosto
 * Use this when a product is added to cart directly from search/category/autocomplete results
 * without navigating to the product page first.
 *
 * @see https://docs.nosto.com/techdocs/implementing-nosto/implement-search/search#analytics
 *
 * @param {string} type - Search type: "serp", "autocomplete", or "category"
 * @param {Object} hit - Product hit object
 * @param {string} hit.productId - The product ID
 * @param {string} hit.url - The product URL
 * @param {Object} [options] - Additional options
 * @param {boolean} [options.debug=false] - Enable debug logging
 * @param {number} [options.timeout=20000] - Request timeout in ms
 * @returns {Promise<void>} - Resolves when tracking is recorded (non-blocking)
 */
export const nostoRecordSearchAddToCart = async (type, hit, { debug = false, timeout = 20000 } = {}) => {
  // debug = true;
  try {
    await nostoAvailableCheck({ timeout });

    return new Promise((resolve, reject) => {
      const timeoutId = setTimeout(() => {
        reject(new Error(`Nosto recordSearchAddToCart request timed out after ${timeout}ms`));
      }, timeout);

      const cleanup = () => {
        clearTimeout(timeoutId);
      };

      window.nostojs((api) => {
        try {
          api.recordSearchAddToCart(type, hit);
          cleanup();
          if (debug) {
            console.log(`Nosto recordSearchAddToCart recorded: ${type}`, hit);
          }
          resolve();
        } catch (error) {
          cleanup();
          reject(new Error(`Nosto recordSearchAddToCart failed for type '${type}': ${error.message || 'Unknown error'}`));
        }
      });
    });
  } catch (error) {
    throw new Error(`Nosto recordSearchAddToCart failed for type '${type}': ${error.message}`);
  }
};

/**
 * Record search product click event with Nosto
 * Use this when a user clicks on a product in search/category/autocomplete results.
 *
 * @see https://docs.nosto.com/techdocs/implementing-nosto/implement-search/search#analytics
 *
 * @param {string} type - Search type: "serp", "autocomplete", or "category"
 * @param {Object} hit - Product hit object
 * @param {string} hit.productId - The product ID
 * @param {string} hit.url - The product URL
 * @param {Object} [options] - Additional options
 * @param {boolean} [options.debug=false] - Enable debug logging
 * @param {number} [options.timeout=20000] - Request timeout in ms
 * @returns {Promise<void>} - Resolves when tracking is recorded (non-blocking)
 */
export const nostoRecordSearchClick = async (type, hit, { debug = false, timeout = 20000 } = {}) => {
  // debug = true;
  try {
    await nostoAvailableCheck({ timeout });

    return new Promise((resolve, reject) => {
      const timeoutId = setTimeout(() => {
        reject(new Error(`Nosto recordSearchClick request timed out after ${timeout}ms`));
      }, timeout);

      const cleanup = () => {
        clearTimeout(timeoutId);
      };

      if (debug) {
        console.log(`Nosto recordSearchClick: ${type}`, hit);
      }

      window.nostojs((api) => {
        try {
          api.recordSearchClick(type, hit);
          cleanup();
          if (debug) {
            console.log(`Nosto recordSearchClick recorded: ${type}`, hit);
          }
          resolve();
        } catch (error) {
          cleanup();
          reject(new Error(`Nosto recordSearchClick failed for type '${type}': ${error.message || 'Unknown error'}`));
        }
      });
    });
  } catch (error) {
    throw new Error(`Nosto recordSearchClick failed for type '${type}': ${error.message}`);
  }
};

/**
 * Record search form submit event with Nosto
 * Use this to mark a search as "organic" - called BEFORE the search request.
 *
 * @see https://docs.nosto.com/techdocs/implementing-nosto/implement-search/search#analytics
 *
 * @param {string} query - The search query
 * @param {Object} [options] - Additional options
 * @param {boolean} [options.debug=false] - Enable debug logging
 * @param {number} [options.timeout=20000] - Request timeout in ms
 * @returns {Promise<void>} - Resolves when tracking is recorded (non-blocking)
 */
export const nostoRecordSearchSubmit = async (query, { debug = false, timeout = 20000 } = {}) => {
  // debug = true;
  try {
    await nostoAvailableCheck({ timeout });

    return new Promise((resolve, reject) => {
      const timeoutId = setTimeout(() => {
        reject(new Error(`Nosto recordSearchSubmit request timed out after ${timeout}ms`));
      }, timeout);

      const cleanup = () => {
        clearTimeout(timeoutId);
      };

      if (debug) {
        console.log(`Nosto recordSearchSubmit:`, query);
      }

      window.nostojs((api) => {
        try {
          api.recordSearchSubmit(query);
          cleanup();
          if (debug) {
            console.log(`Nosto recordSearchSubmit recorded:`, query);
          }
          resolve();
        } catch (error) {
          cleanup();
          reject(new Error(`Nosto recordSearchSubmit failed: ${error.message || 'Unknown error'}`));
        }
      });
    });
  } catch (error) {
    throw new Error(`Nosto recordSearchSubmit failed: ${error.message}`);
  }
};

/**
 * Map Shopify sort options to Nosto sort format
 * @param {string} shopifySort - Shopify sort option (e.g., "price-ascending", "price-descending")
 * @returns {Array|null} - Nosto sort array or null for default relevance
 * @private
 */
const mapShopifySortToNosto = (shopifySort) => {
  // For Nosto, only handle price sorting - all other options use default relevance
  return _sortMapping[shopifySort] || null;
};

/**
 * Build base Nosto query configuration
 * @param {Object} config - Configuration object
 * @param {string} config.context - "search" or "collection"
 * @param {string} config.terms - Search terms (for search context)
 * @param {string|number} config.id - Collection ID (for collection context)
 * @param {string} config.type - Query type: "products", "filters", "predictive"
 * @param {number} config.size - Number of results to return (default: 24)
 * @param {number} config.from - Starting offset (default: 0)
 * @param {Array} config.filters - Additional filters to apply
 * @param {string} config.sortBy - Sort option (default: "relevance")
 * @returns {Object} - Nosto query configuration
 */
export const nostoBuildQuery = ({
  context,
  terms = "",
  categoryId = null,
  type = "products",
  size = 50,
  from = 0,
  filters = [],
  sortBy = "relevance",
  allowSoldOutStateProducts = false
}) => {
  const baseProductsConfig = {
    facets: ["*"],
    fields: _defaultFields[type] || _defaultFields.products,
    size: type === "filters" ? 1 : size, // Minimal size for filter-only queries
    from,
    filter: filters,
    preFilter: _buildPreFilter({ allowSoldOutStateProducts, categoryId: categoryId })
  };

  // Add sorting to the query if not relevance (default)
  if (sortBy && sortBy !== "relevance") {
    const nostoSort = mapShopifySortToNosto(sortBy);
    if (nostoSort) {
      baseProductsConfig.sort = nostoSort;
    }
  }

  if (context === "collection") {
    return {
      query: {
        products: {
          categoryId: String(categoryId),
          ...baseProductsConfig,
        },
      },
      options: { track: "category", redirect: true },
    };
  } else if (context === "search") {
    const baseQuery = {
      query: terms.trim().toLowerCase(),
      products: baseProductsConfig,
    };

    // Add keywords for predictive search
    if (type === "predictive") {
      baseQuery.keywords = {
        fields: _defaultFields.keywords,
        size: 10,
      };
    }

    const queryOptions = { track: type === "predictive" ? "autocomplete" : "serp" };
    // get query params and look for `is_search_suggestion=true`
    const urlParams = new URLSearchParams(window.location.search);
    const isSearchSuggestion = urlParams.get("is_search_suggestion") === "true";
    if (isSearchSuggestion) {
      queryOptions.isKeyword = true;
    }

    queryOptions.redirect = true;

    return {
      query: baseQuery,
      options: queryOptions,
    };
  }

  throw _createNostoError('CONFIGURATION_ERROR', { context }, new Error(`Unsupported context: ${context}`));
};

/**
 * Map Shopify filter parameter to Nosto field name (reverse lookup)
 * @param {string} shopifyFilterParam - Shopify filter parameter
 * @returns {string|null} - Nosto field name or null if not found
 */
export const nostoMapShopifyFilterToNosto = (shopifyFilterParam) => {
  // Create reverse mapping cache on first access for O(1) lookups
  if (!_reverseFilterMap) {
    _reverseFilterMap = new Map();
    for (const [nostoField, shopifyParam] of Object.entries(_filterMap)) {
      _reverseFilterMap.set(shopifyParam, nostoField);
    }
  }

  return _reverseFilterMap.get(shopifyFilterParam) || null;
};

/**
 * Build Nosto filters from Shopify filter parameters
 * @param {Object} activeFilters - Current active filters in Shopify format
 * @param {Object} options - Configuration options
 * @param {Array} options.excludeFilters - Filter parameter names to exclude
 * @param {boolean} options.debug - Enable debug logging
 * @returns {Array} - Nosto filter structure: [] or [{ all: [filter objects] }]
 */
export const nostoBuildFiltersFromShopify = (activeFilters = {}, { excludeFilters = [], debug = false } = {}) => {
  const defaultExcludeFilters = [];

  // Create exclude set for O(1) lookups instead of O(n) array.includes()
  const allExcludeFilters = new Set([...defaultExcludeFilters, ...excludeFilters]);

  // Use Map for O(1) filter lookups instead of O(n) array.find()
  const filtersMap = new Map();

  if (debug) {
    console.log("Building Nosto filters from Shopify activeFilters:", activeFilters);
  }

  // Note: greyson-availability filtering is handled via preFilter in _buildPreFilter()
  // and should not be applied to the main filter query param
  const greysonAvailabilityNostoField = 'skus.customFields.greyson-availability';

  // Convert activeFilters to Nosto format
  for (const [key, values] of Object.entries(activeFilters)) {
    if (Array.isArray(values) && values.length > 0 && !allExcludeFilters.has(key)) {
      // Map Shopify filter parameters to Nosto field names
      const nostoField = nostoMapShopifyFilterToNosto(key);
      if (nostoField) {
        // Transform Shopify boolean values to Nosto format
        const transformedValues = transformShopifyBooleansToNosto(values);

        // Skip greyson-availability filter - it's handled via preFilter
        if (nostoField === greysonAvailabilityNostoField) {
          // Skip adding this filter to the main filter query param
          continue;
        }

        filtersMap.set(nostoField, {
          field: nostoField,
          value: transformedValues,
        });
      }
    }
  }

  const filterConditions = Array.from(filtersMap.values());

  if (debug) {
    console.log("Built Nosto filter conditions:", filterConditions);
    console.log("Note: greyson-availability filtering is handled via preFilter, not main filter query param");
  }

  // Return filters wrapped in 'all' object structure to match preFilter format
  // If no filters, return empty array to avoid sending empty 'all' object
  if (filterConditions.length === 0) {
    return [];
  }

  return filterConditions;
};

/**
 * Extract handle from product URL
 * @param {string} url - Product URL
 * @returns {string} - Product handle
 * @private
 */
const extractHandleFromUrl = (url) => {
  if (!url) return "";

  // Cache handles to avoid repeated URL parsing
  if (!_handleCache) {
    _handleCache = new Map();
  }

  if (_handleCache.has(url)) {
    return _handleCache.get(url);
  }

  const urlParts = url.split("/");
  const handle = urlParts[urlParts.length - 1].split("?")[0];

  _handleCache.set(url, handle);
  return handle;
};

/**
 * Convert a Nosto field name to the corresponding Shopify filter parameter
 * @param {string} nostoField - The Nosto field name (e.g., 'skus.customFields.greyson-color_family')
 * @returns {string|null} - The Shopify filter parameter (e.g., 'filter.v.m.greyson.color_family') or null if no mapping
 */
export const nostoMapFieldToFilter = (nostoField) => {
  // Check direct mapping
  if (_filterMap[nostoField]) {
    return _filterMap[nostoField];
  }

  // Warning for unmapped fields
  console.warn(`No Shopify filter mapping found for Nosto field: ${nostoField}`);
  return null;
};

/**
* Nosto field name to display label mapping for consistent filter display
* Keys are Nosto field names (used in nostoTransformFilterLabel)
*/
const FILTER_LABEL_MAP = {
  // Color - Nosto field names
  "extra.DataExtractor_greyson-available_color_families": "Color",
  "extra.DataExtractor_greyson-available_colors": "Color",
  "skus.customFields.greyson-available_color": "Color",
  "skus.customFields.greyson-available_color_family": "Color",
  "skus.customFields.greyson-color_family": "Color",

  // Size - Nosto field names
  "extra.DataExtractor_greyson-available_sizes": "Size",
  "skus.customFields.greyson-available_size": "Size",

  // Inseam - Nosto field names
  "extra.DataExtractor_greyson-available_inseams": "Inseam",
  "skus.customFields.greyson-available_inseam": "Inseam",

  // Other - Nosto field names
  "customFields.producttype": "Product Type",
  "extra.DataExtractor_custom-department": "Department",
  "customFields.custom-pattern": "Pattern"
};

/**
* Transform Nosto filter label to match unified display standards
* @param {string} filterLabel - The original Nosto filter label
* @param {string} [nostoField] - Optional Nosto field path for better mapping
* @returns {string} - The transformed filter label
*/
export const nostoTransformFilterLabel = (filterLabel, nostoField = null) => {
  if (!filterLabel) return filterLabel;

  // Use endpoint mapping if field path provided
  if (nostoField && FILTER_LABEL_MAP[nostoField]) {
    return FILTER_LABEL_MAP[nostoField];
  }

  // Fallback to legacy transformation for unmapped labels
  const normalizeLabel = (label) => {
    let processed = label
      .replace(/^(?:variant\s+|custom[-_]|dataextractor_custom[-_]|dataextractor_)/i, "");
    return handleize(processed);
  };

  const normalizedLabel = normalizeLabel(filterLabel);

  const labelTransformMap = {
    // Legacy mappings for fields not yet in unified system
    "waist": "Waist",
    "sleeve-length": "Sleeve Length",
    "team": "Team",
    "product-shopify-available": "Availability"
  };

  if (labelTransformMap[normalizedLabel]) {
    return labelTransformMap[normalizedLabel];
  }

  // Apply general transformations for any unmapped labels
  return filterLabel
    .replace(/^(?:Variant\s+|Custom-|custom-|DataExtractor_Custom-|DataExtractor_custom-)/i, "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, l => l.toUpperCase());
};

/**
* Transform a Nosto facet object to match Shopify filter structure
* @param {Object} nostoFacet - The Nosto facet object
* @param {Function} [urlBuilder] - Optional URL builder function (nostoField, value, action) => url
* @returns {Object|null} - Transformed filter object or null if should be skipped
* @private
*/
const transformFacetToFilter = (nostoFacet, urlBuilder) => {
  const shopifyParam = nostoMapFieldToFilter(nostoFacet.field);

  if (!shopifyParam) {
    return null; // Skip this facet
  }

  // Use the field property for accurate mapping, fallback to name
  const mappingKey = nostoFacet.field || nostoFacet.name;

  // Check if this is a price range facet or other special facet type
  if (!nostoFacet.data || !Array.isArray(nostoFacet.data)) {
    return null; // Skip facets without proper data array
  }

  const displayLabel = nostoTransformFilterLabel(nostoFacet.name, nostoFacet.field);

  return {
    activeValues: [],
    label: displayLabel,
    operator: "OR",
    presentation: "text",
    paramName: shopifyParam,
    type: "list",
    values: nostoFacet.data.map(item => ({
      active: item.selected || false,
      count: item.count || 0,
      label: item.value,
      paramName: shopifyParam,
      value: item.value,
      urlToAdd: urlBuilder ? urlBuilder(mappingKey, item.value, "add") : `/collections/all?${shopifyParam}=${encodeURIComponent(item.value)}&view=csr-json`,
      urlToRemove: urlBuilder ? urlBuilder(mappingKey, item.value, "remove") : "/collections/all?view=csr-json"
    }))
  };
};

/**
 * Get the default variant ID for a Nosto product based on availability, sorting, and selected filters
 * @param {Object} product - The Nosto product object
 * @param {string} sortBy - The sort option (e.g., 'price-ascending', 'price-descending', 'relevance')
 * @param {Object} selectedFilters - Object mapping filter keys to selected values (e.g., {'skus.customFields.greyson-color_family': ['Pink']})
 * @param {Object} spotlightConfig - Spotlight selection configuration
 * @returns {number|null} - The default variant ID or null if no available variants
 * @private
 */
const getDefaultVariant = (
  product,
  sortBy = 'relevance',
  selectedFilters = {},
  spotlightConfig = {},
) => {
  if (!product || !product.skus || !Array.isArray(product.skus)) {
    return null;
  }

  // if we are meant to skip the extra variant processing, return the first SKU ID
  if (window.theme?.data?.skip_nosto_variant_processing === true) {
    // Simple mode: just return the first SKU ID
    return product.skus.length > 0 ? parseInt(product.skus[0].id, 10) : null;
  }

  // Cache filter entries and create lookup maps for better performance
  const filterEntries = Object.entries(selectedFilters);
  const hasFilters = filterEntries.length > 0;
  const isPriceSorted = sortBy === 'price-ascending' || sortBy === 'price-descending';
  const isAscending = sortBy === 'price-ascending';
  const shouldUseSpotlight =
    spotlightConfig?.enabled === true &&
    !hasFilters &&
    !isPriceSorted;

  // Pre-process SKUs: filter, create lookup maps, and sort in a single pass
  const processedSkus = [];

  for (let i = 0; i < product.skus.length; i++) {
    const sku = product.skus[i];

    // Create custom fields lookup map for this SKU
    const customFieldsMap = new Map();
    if (sku.customFields) {
      for (let j = 0; j < sku.customFields.length; j++) {
        const field = sku.customFields[j];
        customFieldsMap.set(field.key, field.value);
      }
    }

    // Check availability - skip if not available
    if (customFieldsMap.get('greyson-availability') !== 'true') {
      continue;
    }

    // Apply selected filters if any
    if (hasFilters) {
      // console.log(`Applying selected filters for SKU ${sku.id} for ${product.name}`);
      let matchesAllFilters = true;

      for (const [filterField, selectedValues] of filterEntries) {
        if (!Array.isArray(selectedValues) || selectedValues.length === 0) continue;

        let fieldMatches = false;

        if (filterField.startsWith('skus.customFields.')) {
          const customFieldKey = filterField.slice(18); // 'skus.customFields.'.length = 18
          const fieldValue = customFieldsMap.get(customFieldKey);
          fieldMatches = fieldValue && selectedValues.includes(fieldValue);
        } else if (filterField.startsWith('skus.')) {
          const skuFieldKey = filterField.slice(5); // 'skus.'.length = 5
          fieldMatches = selectedValues.includes(sku[skuFieldKey]);
        } else {
          // Product-level filters don't exclude SKUs
          fieldMatches = true;
        }

        if (!fieldMatches) {
          matchesAllFilters = false;
          break;
        }
      }

      if (!matchesAllFilters) {
        // console.log(`Ignoring SKU ${sku.id} for ${product.name} because it does not match all filters`);
        continue;
      }
    }

    // Add to processed SKUs with pre-parsed price for sorting
    const processedSku = {
      id: sku.id,
      price: isPriceSorted ? (parseFloat(sku.price) || 0) : 0,
      customFieldsMap,
    };

    processedSkus.push(processedSku);
  }

  // Early return if no valid SKUs found
  if (processedSkus.length === 0) {
    //console.log(`No valid SKUs found for ${product.name}`);
    return null;
  }

  if (shouldUseSpotlight) {
    return selectSpotlightVariantId(processedSkus, {
      spotlightColors: spotlightConfig?.colors,
      spotlightColorFamily: spotlightConfig?.colorFamily,
    });
  }

  // Sort if needed (in-place for better performance)
  if (isPriceSorted) {
    //console.log(`Price sorting SKUs for ${product.name}`);
    processedSkus.sort((a, b) => isAscending ? a.price - b.price : b.price - a.price);
  }

  //console.log(`Processed SKUs for ${product.name}:`, processedSkus);

  // Return the first SKU's ID
  return parseInt(processedSkus[0].id, 10);
};

/**
 * Extract selected filters from Nosto facets
 * @param {Array} facets - Array of Nosto facet objects
 * @returns {Object} - Object mapping Nosto field names to arrays of selected values
 * @private
 */
const extractSelectedFilters = (facets = []) => {
  const selectedFilters = {};

  // console.log(`Extracting selected filters from ${facets.length} facets`, facets);

  for (let i = 0; i < facets.length; i++) {
    const facet = facets[i];
    if (facet.data && Array.isArray(facet.data)) {
      const selectedValues = [];
      const facetData = facet.data;

      // Single pass through facet data
      for (let j = 0; j < facetData.length; j++) {
        const item = facetData[j];
        if (item.selected === true) {
          selectedValues.push(item.value);
        }
      }

      if (selectedValues.length > 0) {
        selectedFilters[facet.field] = selectedValues;
      }
    }
  }

  return selectedFilters;
};

const normalizeNostoValue = (value) => {
  if (value === null || value === undefined) return "";
  return handleize(String(value));
};

const getSkuFieldValue = (sku, key) => {
  if (!sku?.customFieldsMap || !key) return "";
  return sku.customFieldsMap.get(key) || "";
};

const selectSpotlightVariantId = (
  processedSkus = [],
  { spotlightColors = [], spotlightColorFamily = "" } = {},
) => {
  if (!Array.isArray(processedSkus) || processedSkus.length === 0) return null;

  const normalizedSpotlightColors = Array.isArray(spotlightColors)
    ? spotlightColors.map((color) => normalizeNostoValue(color)).filter(Boolean)
    : [];

  for (const spotlightColor of normalizedSpotlightColors) {
    const matchingColorSku = processedSkus.find((sku) => {
      const availableColor = normalizeNostoValue(
        getSkuFieldValue(sku, "greyson-available_color"),
      );
      const color = normalizeNostoValue(getSkuFieldValue(sku, "color"));
      return availableColor === spotlightColor || color === spotlightColor;
    });
    if (matchingColorSku?.id) {
      return parseInt(matchingColorSku.id, 10);
    }
  }

  const normalizedSpotlightColorFamily = normalizeNostoValue(spotlightColorFamily);
  if (normalizedSpotlightColorFamily) {
    const matchingFamilySku = processedSkus.find((sku) => {
      const availableColorFamily = normalizeNostoValue(
        getSkuFieldValue(sku, "greyson-available_color_family"),
      );
      const colorFamily = normalizeNostoValue(
        getSkuFieldValue(sku, "greyson-color_family"),
      );
      return (
        availableColorFamily === normalizedSpotlightColorFamily ||
        colorFamily === normalizedSpotlightColorFamily
      );
    });
    if (matchingFamilySku?.id) {
      return parseInt(matchingFamilySku.id, 10);
    }
  }

  const firstAvailableSku = processedSkus[0];
  if (!firstAvailableSku?.id) return null;
  return parseInt(firstAvailableSku.id, 10);
};

/**
 * Create empty results structure
 * @param {Object} config - Configuration options
 * @param {string} config.context - "search" or "collection"
 * @param {string} config.terms - Search terms
 * @returns {Object} - Empty Shopify-compatible structure
 * @private
 */
const createEmptyProductsStructure = ({ context, terms = "" }) => {
  return {
    products: [],
    filters: [],
    resultsCount: 0,
    paginate: {
      currentOffset: 0,
      currentPage: 1,
      items: 0,
      pageSize: 50,
      pages: 0,
      parts: [],
    },
    performed: true,
    terms: context === "search" ? terms : "",
    types: ["product"],
  };
};

/**
 * Transform Nosto response to Shopify format for product grids
 * @param {Object} nostoResponse - Raw Nosto response
 * @param {Object} config - Configuration options
 * @param {string} config.context - "search" or "collection"
 * @param {string} config.terms - Search terms (for search context)
 * @param {number} config.currentPage - Current page number
 * @param {number} config.pageSize - Items per page
 * @param {string} config.sortBy - Current sort option
 * @param {Object} config.spotlightConfig - Spotlight selection configuration
 * @param {Function} config.generatePaginationParts - Function to generate pagination
 * @param {Function} config.getSortOptions - Function to get sort options
 * @returns {Object} - Shopify-compatible data structure
 */
export const nostoTransformToShopifyProducts = (nostoResponse, {
  context,
  terms = "",
  currentPage = 1,
  pageSize = 50,
  sortBy = "relevance",
  spotlightConfig = {},
  generatePaginationParts,
  getSortOptions
}) => {
  if (!nostoResponse?.products?.hits) {
    return createEmptyProductsStructure({ context, terms });
  }

  // Extract selected filters from Nosto facets
  const selectedFilters = extractSelectedFilters(nostoResponse.products?.facets || []);

  // Process products with single pass filtering and mapping
  const products = [];
  const hits = nostoResponse.products.hits;

  for (let i = 0; i < hits.length; i++) {
    const product = hits[i];

    const defaultVariantId = getDefaultVariant(
      product,
      sortBy,
      selectedFilters,
      spotlightConfig,
    );
    if (defaultVariantId) {
      products.push({
        id: parseInt(product.productId, 10),
        handle: extractHandleFromUrl(product.url),
        title: product.name,
        url: product.url,
        imageUrl: product.imageUrl,
        defaultVariantId: parseInt(defaultVariantId, 10),
        tags: product.tags1
      });
    }
  }

  const totalResults = nostoResponse.products.total || 0;
  const totalPages = Math.ceil(totalResults / pageSize);

  // Transform Nosto facets to Shopify filter format with single pass
  const transformedFilters = [];
  const facets = nostoResponse.products?.facets || [];
  for (let i = 0; i < facets.length; i++) {
    const facet = facets[i];
    try {
      const transformedFilter = transformFacetToFilter(facet);
      if (transformedFilter) {
        transformedFilters.push(transformedFilter);
      }
    } catch (error) {
      console.error('Failed to transform Nosto facet to Shopify filter:', error);
    }
  }

  return {
    products,
    filters: transformedFilters,
    resultsCount: totalResults,
    paginate: {
      currentOffset: (currentPage - 1) * pageSize,
      currentPage: currentPage,
      items: totalResults,
      pageSize: pageSize,
      pages: totalPages,
      parts: generatePaginationParts ? generatePaginationParts(totalPages, currentPage) : [],
    },
    performed: true,
    terms: context === "search" ? terms : "",
    types: ["product"],
    sortBy: sortBy,
    sortOptions: getSortOptions ? getSortOptions() : [],
    vendor: "nosto",
  };
};

/**
 * Transform Nosto response to Shopify format for filters only
 * @param {Object} nostoResponse - Raw Nosto response
 * @param {Object} config - Configuration options
 * @param {string} config.sortBy - Current sort option
 * @param {Function} config.getSortOptions - Function to get sort options
 * @param {Function} config.urlBuilder - Function to build filter URLs
 * @returns {Object} - Shopify-compatible filter structure
 */
export const nostoTransformToShopifyFilters = (nostoResponse, {
  sortBy = "relevance",
  getSortOptions,
  urlBuilder
}) => {
  const filters = [];
  const facets = nostoResponse.products?.facets || [];

  for (let i = 0; i < facets.length; i++) {
    const facet = facets[i];
    try {
      const transformedFilter = transformFacetToFilter(facet, urlBuilder);
      if (transformedFilter) {
        filters.push(transformedFilter);
      }
    } catch (error) {
      console.error('Failed to transform Nosto facet to Shopify filter:', error);
      const configError = new Error(`Nosto utilities failed to transform facet: ${facet.name || facet.field}`);
      throw _createNostoError('CONFIGURATION_ERROR', { facet: facet.name || facet.field }, configError);
    }
  }

  return {
    filters,
    sortBy: sortBy,
    sortOptions: getSortOptions ? getSortOptions() : [],
  };
};

/**
 * Transform Nosto response for predictive search
 * @param {Object} nostoResponse - Raw Nosto response
 * @returns {Object} - Predictive search compatible structure
 */
export const nostoTransformToPredictiveSearch = (nostoResponse) => {
  // Match Shopify predictive search format exactly
  const results = {
    vendor: "nosto",
    performed: true,
    resources: {
      articles: [],
      collections: [],
      pages: [],
      products: [],
      queries: []
    },
    terms: nostoResponse.query || "",
    types: []
  };

  // Transform products
  if (nostoResponse.products?.hits) {
    // Extract selected filters from Nosto facets (though typically predictive search may not have active filters)
    const selectedFilters = extractSelectedFilters(nostoResponse.products?.facets || []);

    // Process products with single pass, limiting to 10 items
    const products = [];
    const hits = nostoResponse.products.hits;
    const maxProducts = Math.min(hits.length, 10);

    for (let i = 0; i < maxProducts; i++) {
      const product = hits[i];

      const defaultVariantId = getDefaultVariant(product, 'relevance', selectedFilters);
      if (defaultVariantId) {
        // The image url will be a string such as: "https://cdn.shopify.com/s/files/1/0041/1018/8642/files/YFA24K13LS_476_1_0507.png?v=1734372346"
        const imageUrl = product.imageUrl ? product.imageUrl + '?v=' + Date.now() + '&width=330' : '';
        const imageHtml = imageUrl ?
          `<img src="${imageUrl}" alt="${product.name}" srcset="${imageUrl.replace('&width=330', '&width=165')} 165w" width="165" height="50" class="predictive-search__image">` :
          '';

        products.push({
          id: parseInt(product.productId, 10),
          handle: extractHandleFromUrl(product.url),
          title: product.name,
          url: product.url,
          imageUrl: imageUrl,
          imageHtml: imageHtml,
          defaultVariantId: parseInt(defaultVariantId, 10)
        });
      }
    }

    results.resources.products = products;
    results.types.push('product');
  }

  // Transform keywords/queries
  if (nostoResponse.keywords?.hits) {
    const searchTerm = (nostoResponse.query || "").toLowerCase();
    const queries = [];
    const hits = nostoResponse.keywords.hits;
    const maxQueries = Math.min(hits.length, 10);

    // Pre-compile regex if searchTerm exists for better performance
    let searchRegex = null;
    if (searchTerm) {
      const escapedTerm = searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      searchRegex = new RegExp(`(${escapedTerm})`, 'gi');
    }

    for (let i = 0; i < maxQueries; i++) {
      const keyword = hits[i];
      const keywordText = keyword.keyword;

      // Create styled_text by highlighting the search term
      let styledText = keywordText;
      if (searchRegex && keywordText.toLowerCase().includes(searchTerm)) {
        styledText = keywordText.replace(searchRegex, '<mark>$1</mark>');

        // Wrap non-highlighted parts in spans
        styledText = styledText.replace(/^([^<]+)(?=<mark>)/, '<span>$1</span>');
        styledText = styledText.replace(/(<\/mark>)([^<]+)(?=<mark>|$)/g, '$1<span>$2</span>');
      }

      queries.push({
        text: keywordText,
        styledText: styledText,
        url: `/search?q=${encodeURIComponent(keywordText)}`
      });
    }

    results.resources.queries = queries;
    results.types.push('query');
  }

  return results;
};
